import {useState} from 'react';

import {useNotifications} from '@/providers/components/notificationsProvider';

import Icon from '@/components/icon';
import {Pagination} from '@/components/pagination';

import {NotificationItem} from './NotificationItem';
import {SearchBar} from './SearchBar';
import {SortDropdown, SortOptionT} from './SortDropdown';

function NotificationsList() {
  const {notifications, loading, pagination, searchQuery, searchResults} = useNotifications();

  // State
  const [currentSort, setCurrentSort] = useState<SortOptionT>({
    label: 'Newest First',
    value: 'created_at',
    direction: 'desc',
  });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Icon name="refresh" className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const displayedNotifications = searchQuery ? searchResults.filter(n => n.status !== 'archived') : notifications;

  // Sort notifications
  const sortedNotifications = [...displayedNotifications].sort((a, b) => {
    const {value, direction} = currentSort;
    const multiplier = direction === 'asc' ? 1 : -1;

    switch (value) {
      case 'created_at':
        return multiplier * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'title':
        return multiplier * a.title.localeCompare(b.title);
      case 'type':
        return multiplier * a.type.localeCompare(b.type);
      case 'status':
        return multiplier * a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  if (sortedNotifications.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SearchBar />
          <SortDropdown currentSort={currentSort} onSortChange={setCurrentSort} />
        </div>
        <div className="flex h-64 flex-col items-center justify-center space-y-2 text-center">
          <Icon name="bell" className="text-gray-400" size={48} />
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {searchQuery ? 'No notifications found' : 'No notifications'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {searchQuery ? 'Try a different search term' : "You're all caught up!"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SearchBar />
        <SortDropdown currentSort={currentSort} onSortChange={setCurrentSort} />
      </div>
      {sortedNotifications.map(notification => (
        <NotificationItem key={notification.notification_id} notification={notification} />
      ))}
      {!searchQuery && pagination.totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.setPage}
          />
        </div>
      )}
    </div>
  );
}

export default NotificationsList;
